import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button, Container, Input, Popup, RichTextEditor, FroalaTextEditor } from 'components/UIElements'
import { DEFAULT_LANG, MODAL_BUTTONS_MAP, MODAL_CONTENT_MAP, MODAL_CLASSES_MAP } from 'utils/constants'
import generateLocalesMap, { formatLangCode } from 'utils/locales'
import { compareValues } from 'utils/misc'
import UploadTranslationInput from '../../../../Instruments/routes/InstrumentsPage/components/UploadTranslationInput'

const DEFAULT_LIST = [DEFAULT_LANG]

const LanguageButton = ({ isLatest, onClick, text }) => {
  const ref = useRef()

  useEffect(() => {
    if (isLatest) ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [isLatest])
  return (
    <Button ref={isLatest ? ref : null} className='lang-btn' onClick={onClick}>
      {text}
    </Button>
  )
}

const MessageSection = ({
  addAnnouncementTranslation,
  announcementId,
  clearAnnouncementErrors,
  closeModal,
  content,
  deleteAnnouncementTranslation,
  disabled,
  disabledLangs,
  downloadAnnouncementTranslationTemplate,
  enforcedLanguage,
  errors,
  hasStudyPromotion,
  isSMS,
  locales,
  openModal,
  studyID,
  toggleAnnouncementTranslations,
  translations,
  title,
  updateAnnouncementField,
  updateAnnouncementTranslation,
  uploadAnnouncementTranslations,
  checkedSites,
  sitesList,
}) => {
  const headingRef = useRef(null)

  useEffect(() => {
    return clearAnnouncementErrors
  }, [])

  useEffect(() => {
    if (errors.translations || errors.title || errors.content) {
      headingRef.current.scrollIntoView()
    }
  }, [errors])

  const localesMap = generateLocalesMap(locales)
  const getLocaleLabel = lang => localesMap[lang] || localesMap[formatLangCode(lang)] || lang

  const translationsEnabled = !!(translations && Object.keys(translations).length)
  const [currentLang, setCurrentLang] = useState(DEFAULT_LANG)

  const translationsList = translationsEnabled ? Object.keys(translations) : DEFAULT_LIST
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [deleting, setDeleting] = useState()
  const [subject, setSubject] = useState('')
  const fieldsDisabled = disabledLangs ? disabledLangs.includes(currentLang) : disabled

  useEffect(() => {
    if (!translationsEnabled) return
    if (translations[currentLang]) return

    const normalizedCurrent = formatLangCode(currentLang)
    if (translations[normalizedCurrent]) {
      setCurrentLang(normalizedCurrent)
      return
    }

    const nextLang = translations[DEFAULT_LANG] ? DEFAULT_LANG : Object.keys(translations)[0]
    setCurrentLang(nextLang)
  }, [translations, translationsEnabled, currentLang])

  useEffect(() => {
    if (translationsEnabled) {
      setSubject(translations[currentLang]?.title ?? '')
    } else {
      setSubject(title ?? '')
    }
  }, [translations, translationsEnabled, currentLang, deleting, title])

  const messageContent = translationsEnabled ? translations[currentLang]?.content ?? '' : content
  const translationError = errors.translations?.[currentLang]
  const showContentError =
    translationError?.includes('Content is required') || (!translationsEnabled && !!errors.content)

  const selectedLanguageList = translationsEnabled
    ? translationsList.map((lang, i) => {
        const normalizedLang = formatLangCode(lang)
        const selected = lang === currentLang || normalizedLang === currentLang
        const hasError = (errors.translations && errors.translations[lang]) || (lang === currentLang && errors.content)
        const isDisabledLang = disabledLangs?.includes(lang)
        const isDefaultLang = lang === DEFAULT_LANG || formatLangCode(lang) === DEFAULT_LANG
        return (
          <div
            key={`${localesMap[lang]}-lang-btn-${lang}`}
            className={`lang-btn-wrapper flexed center-aligned${selected ? ' selected' : ''}${
              isDisabledLang ? ' disabled' : ''
            }${isDefaultLang ? ' is-default' : ''}`}>
            {translationsList.length > 1 && !isDefaultLang && !isDisabledLang && (
              <i
                className='fas fa-trash'
                onClick={() => {
                  setCurrentLang(DEFAULT_LANG)
                  setDeleting(!deleting)
                  deleteAnnouncementTranslation(lang)
                }}
              />
            )}
            {selected ? <i className='fas fa-caret-right' /> : <div />}

            <LanguageButton
              className='lang-btn'
              onClick={() => setCurrentLang(lang)}
              text={getLocaleLabel(lang)}
              isLatest={translationsList.length - 1 === i}
            />
            {hasError && <i className='fas fa-exclamation-triangle' />}
          </div>
        )
      })
    : DEFAULT_LIST

  const _toggleAnnouncementTranslations = () => {
    clearAnnouncementErrors()
    setUploadedFileName('')
    if (translationsEnabled) {
      toggleAnnouncementTranslations()
      setCurrentLang(DEFAULT_LANG)
    } else {
      toggleAnnouncementTranslations(DEFAULT_LANG)
    }
  }

  const languageSelectionPopup = () => {
    const currentLanguages = translationsEnabled ? Object.keys(translations) : []
    const useSitesLanguages = enforcedLanguage && !!checkedSites[0]
    // filter the sitesList by the checkedSites and return an array of the site languages without duplicates
    const filterSites = useSitesLanguages
      ? [
          ...new Set(
            (sitesList || [])
              .filter(site => checkedSites.includes(site[0].value))
              .map(siteArr =>
                siteArr
                  .filter(siteOption => siteOption.key === 'enforcedLanguage')
                  .map(option => option.value)
                  .flat(),
              )
              .flat(),
          ),
        ]
      : null
    const languageList = useSitesLanguages
      ? enforcedLanguage?.languages?.filter(language => filterSites.includes(language.key))
      : enforcedLanguage?.languages
      ? enforcedLanguage.languages
      : locales
    const filteredLanguageList = (languageList || []).filter(locale => !currentLanguages.includes(locale.id))
    const languageOptionList = filteredLanguageList.map(({ id, text }) => {
      return (
        <Button
          key={id}
          className='popup-list-btn'
          content={text}
          onClick={() => {
            addAnnouncementTranslation(id)
            setCurrentLang(id)
          }}
        />
      )
    })
    return (
      <Popup
        disabled={languageOptionList.length === 0}
        isButtonList
        noPointer
        position='right'
        trigger={<Button iconBefore='fas fa-plus' id='add-language' content='Add language...' />}>
        {languageOptionList}
      </Popup>
    )
  }

  const onChangeTextEditor = newHtml => {
    if (!translations) {
      updateAnnouncementField('content', newHtml)
    } else {
      updateAnnouncementTranslation({ key: 'content', localeId: currentLang, value: newHtml })
    }
  }

  const _updateAnnouncementTitle = value => {
    setSubject(value)
    if (!translations) {
      updateAnnouncementField('title', value, 'title')
    } else {
      updateAnnouncementTranslation({ key: 'title', localeId: currentLang, value })
    }
  }

  const onOpenUploadModal = () => {
    const successCb = fileName => {
      if (fileName) setUploadedFileName(fileName)
      setCurrentLang(DEFAULT_LANG)
      openModal({
        closeOnBackgroundClick: true,
        content: MODAL_CONTENT_MAP.translationsUploadPrompt,
        confirmButton: MODAL_BUTTONS_MAP.okay,
        className: MODAL_CLASSES_MAP.confirmation,
        hideCancelButton: true,
        onConfirm: closeModal,
      })
    }
    openModal({
      noButtons: true,
      closeOnBackgroundClick: true,
      children: (
        <UploadTranslationInput
          forAnnouncement
          closeModal={closeModal}
          uploadTranslation={file =>
            uploadAnnouncementTranslations({
              studyID,
              instrumentId: announcementId,
              file,
              successCb: () => successCb(file.name),
              enforcedLanguage,
            })
          }
        />
      ),
    })
  }

  if (disabled) selectedLanguageList.sort(compareValues('key'))

  return (
    <>
      <div className='flexed'>
        <h4 className='message-heading' ref={headingRef}>
          Message
        </h4>
        {isSMS || !translationsEnabled || hasStudyPromotion ? (
          <div />
        ) : (
          <div className='flexed'>
            <Button
              light
              iconBefore='fas fa-cloud-download-alt'
              id='download-csv-template'
              content='CSV template'
              onClick={() => {
                downloadAnnouncementTranslationTemplate({
                  studyID,
                  instrumentId: announcementId,
                  title,
                  translations,
                })
              }}
            />
            <Button
              disabled={disabled}
              light
              iconBefore='fas fa-cloud-upload-alt'
              id='import-csv'
              content='Import CSV...'
              onClick={onOpenUploadModal}
            />
          </div>
        )}
      </div>
      <Container>
        {!isSMS ? (
          <div className='flexed announcement-translations-section start-aligned'>
            {translationsEnabled && (
              <div className='flexed column language-sidebar side-button-list'>
                <div className='lang-list'>{selectedLanguageList}</div>
                {!hasStudyPromotion && languageSelectionPopup()}
              </div>
            )}
            <div className='message-content'>
              <Input
                id='annoucement-subject'
                charLimit={100}
                className='announcement-subject'
                hasError={errors.translations ? !!errors.translations[currentLang] : errors.title || !!errors.content}
                errorText={errors.translations ? errors.translations[currentLang] : errors.title || errors.content}
                disabled={fieldsDisabled}
                onChange={_updateAnnouncementTitle}
                value={subject}
                label='Subject'
              />
              <p className='label-small'>{isSMS ? 'SMS Text' : 'Announcement Content'}</p>
              {showContentError && <p className='error-text'>Content is required</p>}
              <FroalaTextEditor
                value={messageContent}
                onChange={onChangeTextEditor}
                autoFocus
                hasBlockStyles
                readOnly={fieldsDisabled}
                spellCheck
                moreText={{
                  includeAll: true,
                }}
                moreParagraph={{
                  includeAll: true,
                }}
                moreRich={{
                  buttons: ['insertLink', 'insertTable', 'emoticons', 'specialCharacters'],
                  includeAll: false,
                }}
                placeholder='Type choice'
              />
              {translationsEnabled && uploadedFileName && (
                <div className='uploaded-filename flexed end-justified center-aligned'>
                  <i className='fas fa-check-circle' />
                  <span>{` Imported from ${uploadedFileName}`}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            <p className='label-small'>{isSMS ? 'SMS Text' : 'Announcement Content'}</p>
            <FroalaTextEditor
              value={content}
              onChange={newHtml => updateAnnouncementField('content', newHtml)}
              readOnly={disabled}
              showToolbar={!isSMS}
              wordCounterCount={false}
            />
            <Input
              charLimit={30}
              id='message-label-input'
              className='sms-label'
              hasError={errors.title}
              errorText={errors.title}
              disabled={disabled}
              onChange={value => updateAnnouncementField('title', value, 'title')}
              value={title}
              label='Message Label'
              subTitle='Participants will not see this'
            />
          </>
        )}
        {!disabled && !isSMS && !hasStudyPromotion && (
          <Button
            link
            id={`${translationsEnabled ? 'clear-all' : 'include'}-translations`}
            className='translation-toggle'
            content={`${translationsEnabled ? 'Clear all' : 'Include'} translations`}
            onClick={_toggleAnnouncementTranslations}
          />
        )}
      </Container>
    </>
  )
}

LanguageButton.propTypes = {
  isLatest: PropTypes.bool,
  onClick: PropTypes.func,
  text: PropTypes.string,
}

MessageSection.propTypes = {
  addAnnouncementTranslation: PropTypes.func,
  announcementId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  clearAnnouncementErrors: PropTypes.func,
  closeModal: PropTypes.func,
  deleteAnnouncementTranslation: PropTypes.func,
  disabled: PropTypes.bool,
  disabledLangs: PropTypes.arrayOf(PropTypes.string),
  downloadAnnouncementTranslationTemplate: PropTypes.func,
  enforcedLanguage: PropTypes.shape({
    default_language: PropTypes.string,
    languages: PropTypes.arrayOf(PropTypes.object),
  }),
  errors: PropTypes.shape({ title: PropTypes.string, content: PropTypes.string, translations: PropTypes.object }),
  isSMS: PropTypes.bool,
  locales: PropTypes.arrayOf(PropTypes.object),
  openModal: PropTypes.func,
  studyID: PropTypes.number,
  title: PropTypes.string,
  toggleAnnouncementTranslations: PropTypes.func,
  translations: PropTypes.objectOf(PropTypes.object),
  updateAnnouncementField: PropTypes.func,
  updateAnnouncementTranslation: PropTypes.func,
  uploadAnnouncementTranslations: PropTypes.func,
  checkedSites: PropTypes.array,
  sitesList: PropTypes.array,
}

export default MessageSection
